/**
 * The guided walkthrough — every step of the UI explained for someone who has
 * never touched a Kaoss Pad.
 *
 * ── MAINTENANCE RULE (enforced by test/tour.test.ts) ─────────────────────────
 * Every UI feature ships with its tour step. When you add or change a control:
 *   1. add/update a step here (plain data — no code needed),
 *   2. update the keyboard overlay in KpvUi.toggleHelp() if it has a key,
 *   3. if it's a new highlight region, add its name to TOUR_TARGET_NAMES and
 *      register the control in KpvUi.tourTargets().
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Every region the tour can spotlight. KpvUi registers a control for each. */
export const TOUR_TARGET_NAMES = [
  'pad',
  'programHeader',
  'browser',
  'memories',
  'deckRow',
  'bpm',
  'perfRow',
  'motionRelease',
  'fxDepth',
  'metersGains',
  'input',
  'samplerMode',
  'banks',
  'slices',
] as const;

export type TourTargetName = (typeof TOUR_TARGET_NAMES)[number];

export interface TourStep {
  /** Region to spotlight; omit for full-screen steps (welcome / finish). */
  target?: TourTargetName;
  title: string;
  body: string;
}

export const TOUR_STEPS: TourStep[] = [
  {
    title: 'Welcome to your KAOSS PAD',
    body:
      'This is a full Korg KAOSS PAD KPV in your browser — an effects processor, ' +
      'sampler, and looper you play by touch.\n\n' +
      'The idea in one line: sound comes IN (your mic, an instrument, or loops ' +
      'you record), you TOUCH the big pad to warp it with effects, and it goes OUT ' +
      'to your speakers.\n\nThis tour walks through every control. Enter or ' +
      'Next ▶ advances · ← goes back · Esc closes.',
  },
  {
    target: 'pad',
    title: 'The pad — where you play',
    body:
      'Touch and drag: the effect is ON while your finger is down, OFF when you ' +
      'lift (with a musical echo tail as you release).\n\n' +
      'The X and Y position each control a parameter — the header above always ' +
      'tells you which two. Left↔right and up↔down are your instrument.\n\n' +
      'No sound yet? Hit DEMO (bottom bar) to load a drum loop, then touch here.',
  },
  {
    target: 'programHeader',
    title: 'The current program',
    body:
      'A "program" is one effect setup — this shows the one that is loaded.\n\n' +
      'Under the name: exactly what X and Y do on the pad, and what a second ' +
      'finger (T2) would control.\n\n' +
      'The little dot is honesty: green = this program is fully modeled, ' +
      'yellow = partially. Click any program name on the left to switch.',
  },
  {
    target: 'browser',
    title: '170 programs, 11 flavors',
    body:
      'Every factory program from the real KPV, grouped by family:\n\n' +
      'Filter (sweeps), Color (distortion & crunch), Mod, LFO (rhythmic ' +
      'wobbles), Delay (echoes), Reverb (space), Looper (beat-repeat rolls), ' +
      'Kaoss (wild combos), Vocal (hardtune), Drum & Synth (they make their own ' +
      'sound).\n\nClick a category, click a program. Arrow keys step through them.',
  },
  {
    target: 'memories',
    title: 'Memories M1–M8',
    body:
      'Your favorite setups, one click away.\n\n' +
      'CLICK a slot to recall it. HOLD one for a second to save what you have ' +
      'right now — the program, the FX depth, even a recorded pad motion.\n\n' +
      'They survive reloads. The slot lights up when its program is active.',
  },
  {
    target: 'deckRow',
    title: 'Decks — more than one KPV',
    body:
      '+ DECK spawns a second full KPV with its own pad, programs, and sample ' +
      'banks — play both at once.\n\n' +
      'CHAIN (default): deck 1 flows INTO deck 2 — stack a filter into a ' +
      'reverb. PARALLEL: they play side by side.\n\n' +
      'Touch a pad to make it the "focused" deck — every panel follows. Tab ' +
      'switches focus. Hold a deck chip to remove it. All decks share one tempo.',
  },
  {
    target: 'bpm',
    title: 'Tempo — one clock for everything',
    body:
      'Everything synced (loops, delays, LFOs) follows this BPM, across all ' +
      'decks.\n\nSet it by: TAP (tap the button on the beat), the +/− buttons, ' +
      'or , and . keys.\n\nOr do not set it at all — your FIRST sample recording ' +
      'will define it: record freely, punch out, and the app derives the tempo ' +
      'from your take. MIDI clock from other gear is followed automatically.',
  },
  {
    target: 'perfRow',
    title: 'HOLD · MUTE · T2',
    body:
      'HOLD (Space): latches the effect exactly where you lifted your finger — ' +
      'set a filter and leave it singing.\n\n' +
      'MUTE (hold M or press-and-hold the button): kills the dry signal but ' +
      'lets effect tails ring — the classic DJ "transformer" cut.\n\n' +
      'T2 (F): enables the second finger. Off by default so stray touches ' +
      'never fire sub-effects. On desktop, Shift-drag is finger two.',
  },
  {
    target: 'motionRelease',
    title: 'Motion recorder & FX Release',
    body:
      '● MOTION (R): records your pad gesture, then replays it as a loop, in ' +
      'tempo — a ghost finger playing your move while you do something else. ' +
      'Your real finger always overrides it. Hold the chip to erase.\n\n' +
      'FX RELEASE: how much echo trails off when you lift your finger — a ' +
      'tempo-synced tail. Slide it up for dubby throws.',
  },
  {
    target: 'fxDepth',
    title: 'FX Depth',
    body:
      'How much of the effect you get when touching: full wet at the top, ' +
      'subtle blend lower down.\n\nKeys: [ and ]. This is per-deck — the ' +
      'hardware has this exact slider.',
  },
  {
    target: 'metersGains',
    title: 'Levels: meters, IN GAIN, MON, OUT VOL',
    body:
      'The meters show input (cyan) and output (orange). The IN meters show the ' +
      'level a RECORDING would capture.\n\n' +
      'IN GAIN: boosts/cuts your input into the machine.\n' +
      'MON: how loud you HEAR yourself — click MON (or press N) to switch ' +
      'monitoring off entirely. Recording is not affected; takes always capture ' +
      'full level and come back auto-leveled.\n' +
      'OUT VOL: the master.',
  },
  {
    target: 'input',
    title: 'Getting sound in',
    body:
      'CONNECT INPUT asks the browser for your mic or audio interface; click it ' +
      'again to cycle between devices.\n\n' +
      'You can also just DRAG an audio file anywhere onto the window — it lands ' +
      'in the next free sample bank and starts looping.\n\n' +
      'MIDI gear works too: anything sending the hardware KPV control map plays ' +
      'this app, and incoming MIDI clock sets the tempo.',
  },
  {
    target: 'samplerMode',
    title: 'Recording a loop',
    body:
      'The flow, left to right:\n\n' +
      '1. Press ● SAMPLE (or S) — it arms and turns red.\n' +
      '2. LEN: how many beats to record (auto-stops exactly on the beat).\n' +
      '3. SRC: IN = record your input · RESAMPLE = record what you HEAR — ' +
      'effects, loops, everything. Bounce your whole jam into one loop.\n' +
      '4. Press a bank letter — recording starts, then loops in sync.\n\n' +
      'No tempo set yet? It records until you press again — and that take ' +
      'becomes the tempo.',
  },
  {
    target: 'banks',
    title: 'Sample banks A–H',
    body:
      'Each letter is a loop slot (keys 1–8). Press to play, press to stop — ' +
      'loops always drop back in ON the beat, phase-locked to everything else. ' +
      'Toggle freely; they can never drift apart.\n\n' +
      'Amber = loaded, green = playing, red = recording (press to punch out). ' +
      'The thin strip underneath is the playhead. + adds more banks.',
  },
  {
    target: 'slices',
    title: 'The chop grid — polyrhythm machine',
    body:
      'Your selected bank, cut into 8 or 16 slices with its waveform drawn in. ' +
      'CLICK a cell to kill that slice (Shift+1–8 live).\n\n' +
      'SKIP (default): killed slices are cut OUT of time — the loop gets ' +
      'shorter and re-grooves. This is where polyrhythms live.\n' +
      'MUTE: keeps the length, leaves silent gaps.\n\n' +
      'LOOP/ONE toggles looping vs one-shot. ✕ HOLD: hold a full second to ' +
      'clear the bank (so you can never wipe one by accident).',
  },
  {
    title: 'That is everything — go play',
    body:
      'Quick recipe to start: DEMO → touch the pad → try dLY.1, LoP.1, rEv.1 ' +
      '→ press S, RESAMPLE, LEN 4, hit bank B — you just bounced your jam.\n\n' +
      'Press ? anytime for the keyboard cheat sheet. This ? corner button ' +
      'replays the tour.\n\nEverything runs locally on your machine. On an ' +
      'iPad (via dev:lan) the pad is true multi-touch. Have fun.',
  },
];
